import { forwardRef, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Slide,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { Close, Warning, Description, CloudUpload } from '@mui/icons-material';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProfileCompletionAlert = ({
  openAlert,
  onCloseAlert,
  title,
  message,
  buttonText,
  onButtonClick,
  showUpload = false, // New prop to control upload UI visibility
  errorMessage = null, // Error message from backend
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    setSelectedFile(file);
  };

  const handleApply = () => {
    onButtonClick(selectedFile);
  };

  return (
    <Dialog
      open={openAlert}
      slots={{ transition: Transition }}
      keepMounted
      onClose={onCloseAlert}
      aria-describedby="alert-dialog-slide-description"
      fullWidth
      maxWidth="xs"
    >
      <IconButton
        sx={{ position: 'absolute', right: 10, top: 10 }}
        onClick={onCloseAlert}
      >
        <Close />
      </IconButton>

      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          paddingBottom: 1,
          gap: 1,
          bgcolor: 'background.paper',
        }}
      >
        {!showUpload && (
          <Avatar
            sx={{
              width: 60,
              height: 60,
              backgroundColor: 'warning.main',
            }}
          >
            <Warning sx={{ color: 'white', fontSize: 36 }} />
          </Avatar>
        )}
        {title}
      </DialogTitle>

      <DialogContent
        sx={{ textAlign: 'center', paddingBottom: 1, bgcolor: 'background.paper' }}
      >
        {!showUpload ? (
          <DialogContentText id="alert-dialog-slide-description">
            {message}
          </DialogContentText>
        ) : (
          <Box>
            <DialogContentText sx={{ mb: 3 }}>
              {message}
            </DialogContentText>

            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                padding: 4,
                textAlign: 'center',
                bgcolor: dragActive ? 'action.hover' : 'background.default',
                transition: 'all 0.3s',
                cursor: 'pointer',
              }}
            >
            

              <Typography variant="h6" fontWeight={500} gutterBottom>
                {selectedFile ? selectedFile.name : 'Drag & Drop your CV here'}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or
              </Typography>

              <input
                type="file"
                id="cv-upload"
                accept=".pdf,.doc,.docx"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              <label htmlFor="cv-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  disableElevation
                  sx={{ textTransform: 'none' }}
                >
                  Browse Files
                </Button>
              </label>
            </Box>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: 'center', paddingBottom: 1.5, bgcolor: 'background.paper' }}
      >
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          disableElevation
          sx={{ textTransform: 'none' }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileCompletionAlert;
